import { useApolloClient } from "@apollo/client";
import { analytics } from "_shared/utils/analytics";
import { API, Auth, graphqlOperation } from "aws-amplify";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { useSetUserStatusMutation } from "../graphql";
import useLocalStorage from "../hooks/useLocalStorage";
import { useModal } from "./ModalContext";
import { usePostHog } from "posthog-js/react";

const AuthContext = createContext({
  isAuthenticated: false,
  isAuthenticating: false,
  signIn: async (
    _username: string,
    _password: string,
    _rememberMe: boolean,
  ) => { },
  signOut: async () => { },
  forgotPassword: async (_username: string) => { },
  resetPassword: async (
    _username: string,
    _code: string,
    _password: string,
  ) => { },
  savedUser: null as string | null,
  rememberMe: false,
});

export const AuthProvider: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const history = useHistory();
  const { onErrorMsg, awaitConfirmation } = useModal();

  const [rememberMe, setRememberMe] = useLocalStorage("rememberMe", false);
  const [savedUser, setSavedUser] = useState<string | null>(null);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  const [setUserStatus] = useSetUserStatusMutation();

  const client = useApolloClient();

  const posthog = usePostHog();

  // check if user has token on load
  useEffect(() => {
    const checkSession = async () => {
      setIsAuthenticating(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        posthog?.identify(user?.attributes?.sub, { email: user?.attributes?.email });
        const { accessToken, refreshToken } = await user.getSignInUserSession();
        const expiryInSeconds = await accessToken.getExpiration();
        const nowInSeconds = Math.floor(Date.now() / 1000);
        // revoke auth if remember me is false
        if (!rememberMe) {
          await Auth.signOut();
          return setIsAuthenticating(false);
        }
        // refresh current user's session
        await user.refreshSession(
          refreshToken,
          async (_err: any, session: any) => {
            // sign user out if refresh token has been revoked
            if (!session) {
              Auth.signOut();
              return setIsAuthenticating(false);
            }
            // accept logged in user if current accessToken is valid (default 1h)
            if (expiryInSeconds > nowInSeconds) {
              setIsAuthenticated(true);
              return setIsAuthenticating(false);
            }
            setSavedUser(user!.getUsername());
            return;
          },
        );
      } catch (error: any) {
        console.error(error);
        setIsAuthenticating(false);
      }
    };
    checkSession();
  }, []); // eslint-disable-line

  useEffect(() => {
    const setIdentity = async () => {
      const user = await Auth.currentUserInfo();
      analytics.identify(user.email);
    };
    if (isAuthenticated) {
      setIdentity();
    } else {
      client.clearStore();
      analytics.reset();
    }
  }, [isAuthenticated]);

  const signIn = useCallback(
    async (username: string, password: string, rememberMe: boolean) => {
      setIsAuthenticating(true);
      setRememberMe(rememberMe);

      try {
        // bypass actual sign in -- user is already signed in in the background
        if (savedUser && password === "savedpassword") {
          setSavedUser(null);
          setIsAuthenticating(false);
          return setIsAuthenticated(true);
        }

        await Auth.signIn(username, password);

        const sessionsRes = await API.graphql<any>(
          graphqlOperation(`{ users { is_active } }`),
        );
        const hasActiveSession = sessionsRes.data.users![0]?.is_active ?? false;

        if (hasActiveSession) {
          setIsAuthenticating(false);
          const signoutAndPause = async () => {
            setIsAuthenticating(true);
            // TODO: amplify bug? does not resolve
            await Auth.signOut({ global: true });
            // workaround to allow global sign out to work before signing back in
            return await new Promise((resolve) =>
              setTimeout(() => {
                resolve(true);
              }, 4000),
            );
          };
          const confirmed = await awaitConfirmation(
            signoutAndPause,
            <>
              <p>
                We have detected that you are currently logged in to Longbow
                from another computer.
              </p>
              <p>
                To continue with this session, we'll have to sign you out of the
                other session.
              </p>
              <p>Click 'Confirm' to proceed.</p>
            </>,
          );
          if (!confirmed) return;
          await Auth.signIn(username, password);

          const user = await Auth.currentAuthenticatedUser();
          posthog?.identify(user?.attributes?.sub, { email: user?.attributes?.email });
        }

        await setUserStatus({ variables: { active: true } });

        // TODO: this requires a change in the user pool that will
        // affect the trigger on our `cognito` lambda function
        // if (rememberMe) await Auth.rememberDevice();
        // else await Auth.forgetDevice();

        setIsAuthenticated(true);
        history.push("/dashboard");
      } catch (err: any) {
        console.error(JSON.stringify(err));
        if (err.code === "UserNotConfirmedException") {
          // props.updateUsername(username);
          await Auth.resendSignUp(username);
          // props.onStateChange("confirmSignUp", {});
        } else if (err.code === "NotAuthorizedException") {
          // incorrect password provided
          onErrorMsg(err.message);
        } else if (err.code === "UserNotFoundException") {
          // supplied username/email does not exist in the Cognito user pool
          onErrorMsg("Login failed.");
        } else {
          onErrorMsg(`An error occurred: ${JSON.stringify(err)}`);
        }
      }
      setIsAuthenticating(false);
    },
    [
      history,
      onErrorMsg,
      savedUser,
      awaitConfirmation,
      setRememberMe,
      setUserStatus,
    ],
  );

  const signOut = useCallback(async () => {
    setIsAuthenticated(false);
    try {
      await Auth.signOut();
      // sets user as logged out in db
      await setUserStatus({ variables: { active: false } });
    } catch (err) {
      console.log("signout error", JSON.stringify(err, null, 2));
    }
  }, [setUserStatus]);

  const forgotPassword = useCallback(
    async (username: string) => {
      try {
        await Auth.forgotPassword(username);
      } catch (error) {
        onErrorMsg("We couldn't reset the password for that email address");
        console.error(error);
      }
    },
    [onErrorMsg],
  );

  const resetPassword = useCallback(
    async (username: string, code: string, password: string) => {
      try {
        await Auth.forgotPasswordSubmit(username, code, password);
      } catch (error) {
        throw error;
      }
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthenticating,
        signIn,
        signOut,
        forgotPassword,
        resetPassword,
        savedUser,
        rememberMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

type AuthValues = ReturnType<typeof useAuth>;

// export as HOC to help with unit testing components in isolation
export function withAuth<T extends Partial<AuthValues>>(
  Component: React.FC<T>,
) {
  // spread other props to the component along with context values
  return function ComponentWithAuth(props: Omit<T, keyof AuthValues>) {
    const values = useAuth();
    return <Component {...(props as T)} {...values} />;
  };
}
